import React, { useState, useEffect } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import apiService from "../../shared/apiService";
import {
  X,
  DollarSign,
  Users,
  TrendingUp,
  Search,
  MapPin,
  Zap,
  Plus,
  Linkedin,
  Briefcase,
  Building,
  GraduationCap,
  Globe,
  FileText,
  HelpCircle,
  ChevronDown,
} from "lucide-react";
import { cn } from "../../utils/cn";

interface BoostLinkedInModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: any) => Promise<void>;
  loading: boolean;
  post: any;
}

const BoostLinkedInModal: React.FC<BoostLinkedInModalProps> = ({
  open,
  onClose,
  onConfirm,
  loading,
  post,
}) => {
  const { t } = useTranslation();
  const [dailyBudget, setDailyBudget] = useState(100);
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState(
    new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  );
  const [objective, setObjective] = useState("impressions");
  const [language, setLanguage] = useState("pt");

  // LinkedIn Specific States
  const [accounts, setAccounts] = useState<any[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  const [leadForms, setLeadForms] = useState<any[]>([]);
  const [selectedLeadFormId, setSelectedLeadFormId] = useState<string>("");
  const [loadingLeadForms, setLoadingLeadForms] = useState(false);

  // Toggles
  const [audienceExpansion, setAudienceExpansion] = useState(true);
  const [audienceNetwork, setAudienceNetwork] = useState(true);
  const [showAdvancedTargeting, setShowAdvancedTargeting] = useState(false);

  // Reach Estimate
  const [reachEstimate, setReachEstimate] = useState<any>(null);
  const [loadingEstimate, setLoadingEstimate] = useState(false);

  // Targeting States
  const [locationSearch, setLocationSearch] = useState("");
  const [selectedLocations, setSelectedLocations] = useState<any[]>([
    { urn: "urn:li:geo:106057199", name: "Brasil" },
  ]);
  const [locationResults, setLocationResults] = useState<any[]>([]);
  const [searchingLocations, setSearchingLocations] = useState(false);

  const [jobTitleSearch, setJobTitleSearch] = useState("");
  const [selectedJobTitles, setSelectedJobTitles] = useState<any[]>([]);
  const [jobTitleResults, setJobTitleResults] = useState<any[]>([]);
  const [searchingJobTitles, setSearchingJobTitles] = useState(false);

  const [industrySearch, setIndustrySearch] = useState("");
  const [selectedIndustries, setSelectedIndustries] = useState<any[]>([]);
  const [industryResults, setIndustryResults] = useState<any[]>([]);
  const [searchingIndustries, setSearchingIndustries] = useState(false);

  const [senioritySearch, setSenioritySearch] = useState("");
  const [selectedSeniorities, setSelectedSeniorities] = useState<any[]>([]);
  const [seniorityResults, setSeniorityResults] = useState<any[]>([]);
  const [searchingSeniorities, setSearchingSeniorities] = useState(false);

  // Load Accounts
  useEffect(() => {
    if (open) {
      const fetchAccounts = async () => {
        setLoadingAccounts(true);
        try {
          const data = await apiService.get("/ads/linkedin/accounts");
          if (data.success) {
            setAccounts(data.accounts || []);
            if (data.accounts?.length > 0) {
              setSelectedAccountId(data.accounts[0].id);
            }
          }
        } catch (err) {
          console.error("Erro ao carregar contas LinkedIn:", err);
        } finally {
          setLoadingAccounts(false);
        }
      };
      fetchAccounts();
    }
  }, [open]);

  // Load Lead Forms
  useEffect(() => {
    if (open && selectedAccountId && objective === "leads") {
      const fetchForms = async () => {
        setLoadingLeadForms(true);
        try {
          const data = await apiService.get("/ads/linkedin/leadforms", {
            account_urn: selectedAccountId,
          });
          if (data.success) {
            setLeadForms(data.forms || []);
            if (data.forms?.length > 0) {
              setSelectedLeadFormId(data.forms[0].id);
            }
          }
        } catch (err) {
          console.error("Erro ao carregar formulários:", err);
        } finally {
          setLoadingLeadForms(false);
        }
      };
      fetchForms();
    }
  }, [open, selectedAccountId, objective]);

  // Fetch Reach Estimate
  useEffect(() => {
    if (!open || !selectedAccountId) return;

    const fetchEstimate = async () => {
      setLoadingEstimate(true);
      try {
        const targeting = {
          locations: selectedLocations.map((l) => l.urn),
          jobTitles: selectedJobTitles.map((t) => t.urn),
          industries: selectedIndustries.map((i) => i.urn),
          seniorities: selectedSeniorities.map((s) => s.urn),
          language: language,
        };

        const data = await apiService.post("/ads/linkedin/reach-estimate", {
          account_urn: selectedAccountId,
          targeting,
          budget: dailyBudget,
        });

        if (data.success) {
          setReachEstimate(data.estimate);
        }
      } catch (err) {
        console.error("Erro ao carregar estimativa:", err);
      } finally {
        setLoadingEstimate(false);
      }
    };

    const timer = setTimeout(fetchEstimate, 1000);
    return () => clearTimeout(timer);
  }, [
    selectedAccountId,
    selectedLocations,
    selectedJobTitles,
    selectedIndustries,
    selectedSeniorities,
    dailyBudget,
    language,
    open,
  ]);

  // Generic Targeting Search Effect
  const useTargetingSearch = (
    query: string,
    facet: string,
    setResults: any,
    setSearching: any,
  ) => {
    useEffect(() => {
      const timer = setTimeout(async () => {
        if (query.length >= 2) {
          setSearching(true);
          try {
            const data = await apiService.get(
              "/ads/linkedin/targeting/search",
              {
                facet,
                q: query,
              },
            );
            if (data.success) {
              setResults(data.elements || []);
            }
          } catch (err) {
            console.error(`Erro na busca de ${facet}:`, err);
          } finally {
            setSearching(false);
          }
        } else {
          setResults([]);
        }
      }, 500);
      return () => clearTimeout(timer);
    }, [query]);
  };

  useTargetingSearch(
    locationSearch,
    "locations",
    setLocationResults,
    setSearchingLocations,
  );
  useTargetingSearch(
    jobTitleSearch,
    "jobTitles",
    setJobTitleResults,
    setSearchingJobTitles,
  );
  useTargetingSearch(
    industrySearch,
    "industries",
    setIndustryResults,
    setSearchingIndustries,
  );
  useTargetingSearch(
    senioritySearch,
    "seniorities",
    setSeniorityResults,
    setSearchingSeniorities,
  );

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const getDurationInDays = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diff = end.getTime() - start.getTime();
    return Math.max(1, Math.ceil(diff / (1000 * 3600 * 24)));
  };

  const duration = getDurationInDays();
  const totalBudget = dailyBudget * duration;

  const handleSubmit = async () => {
    await onConfirm({
      name: `Boost: ${post.content?.substring(0, 30) || t("Post LinkedIn")}`,
      platform: "linkedin",
      objective: objective === "impressions" ? "awareness" : objective,
      budget: {
        amount: totalBudget,
        currency: "BRL",
        daily_limit: dailyBudget,
      },
      target_audience: {
        locations: selectedLocations.map((l) => l.urn),
        job_titles: selectedJobTitles.map((t) => t.urn),
        industries: selectedIndustries.map((i) => i.urn),
        seniorities: selectedSeniorities.map((s) => s.urn),
        language,
      },
      approved_post_id: post.id,
      account_urn: selectedAccountId,
      start_date: new Date(startDate).toISOString(),
      end_date: new Date(endDate).toISOString(),
      draft_data: {
        lead_form_urn: objective === "leads" ? selectedLeadFormId : undefined,
        audience_expansion: audienceExpansion,
        audience_network: audienceNetwork,
        language,
        locations: selectedLocations.map((l) => l.urn),
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate).toISOString(),
      },
    });
  };

  const addTarget = (
    item: any,
    selected: any[],
    setSelected: any,
    setQuery: any,
    setResults: any,
  ) => {
    if (!selected.find((i) => i.urn === item.urn)) {
      setSelected([...selected, item]);
    }
    setQuery("");
    setResults([]);
  };

  // Helper for dynamic estimation calculations if API response is not ready
  const getAudienceSize = () => {
    if (reachEstimate?.audienceSize) {
      return `${(reachEstimate.audienceSize / 1000).toFixed(0)}k+`;
    }
    return t("580.000+");
  };

  const getImpressionsRange = () => {
    if (reachEstimate?.impressions) {
      return `${reachEstimate.impressions[0]} - ${reachEstimate.impressions[1]}`;
    }
    // Dynamic authentic range based on budget
    const low = Math.floor(dailyBudget * duration * 8);
    const high = Math.floor(dailyBudget * duration * 35);
    return `${low.toLocaleString("pt-BR")} - ${high.toLocaleString("pt-BR")}`;
  };

  const getPostImageUrl = () => {
    if (post.image_info?.public_url) return post.image_info.public_url;
    if (post.image_info?.filename) return post.image_info.filename;
    if (post.media_gallery && post.media_gallery.length > 0) {
      return post.media_gallery[0].public_url || post.media_gallery[0].url;
    }
    return null;
  };

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center bg-purple-900/40 backdrop-blur-md animate-in fade-in duration-300 p-4">
      <div className="bg-[#f3f2ef] w-full max-w-6xl rounded-3xl shadow-[0_32px_128px_rgba(0,0,0,0.15)] overflow-hidden flex flex-col animate-in zoom-in-95 duration-500 max-h-[95vh] border border-gray-200">
        {/* Styled Professional Header */}
        <div className="px-10 py-8 border-b border-gray-200 flex items-center justify-between bg-white shadow-xs">
          <div className="flex items-center gap-6">
            <div className="p-5 bg-[#0077b5] rounded-[24px] shadow-2xl shadow-[#0077b5]/20 ring-8 ring-[#0077b5]/5">
              <Linkedin className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-gray-900 tracking-tighter uppercase leading-none">
                {t("Sponsor LinkedIn Post")}
              </h2>
              <p className="text-[11px] text-gray-400 font-bold uppercase tracking-widest mt-2 border-l-2 border-purple-200 pl-4">
                {t("Profissional • LinkedIn Ads Integration")}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-3 hover:bg-gray-100 rounded-full transition-all text-gray-400 hover:text-gray-950"
          >
            <X className="w-7 h-7" />
          </button>
        </div>

        {/* Content Area - 2 Columns */}
        <div className="p-10 space-y-8 overflow-y-auto max-h-[75vh] custom-scrollbar">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Column (Configuration) - 7 cols */}
            <div className="lg:col-span-7 flex flex-col gap-6">
              {/* Account Selector Card */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xs flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                      <Building className="w-3.5 h-3.5 text-[#0077b5]" />{" "}
                      {t("Conta de Anúncios")}
                    </label>
                    <div className="relative">
                      <select
                        value={selectedAccountId}
                        onChange={(e) => setSelectedAccountId(e.target.value)}
                        className="w-full h-12 px-4 bg-white border border-gray-300 rounded-xl text-sm font-semibold text-gray-900 outline-hidden focus:border-[#0077b5] focus:ring-2 focus:ring-[#0077b5]/10 appearance-none transition-all"
                      >
                        {loadingAccounts ? (
                          <option>{t("Carregando contas...")}</option>
                        ) : accounts.length > 0 ? (
                          accounts.map((acc) => (
                            <option key={acc.id} value={acc.id}>
                              {acc.name} ({acc.currency})
                            </option>
                          ))
                        ) : (
                          <option>{t("Nenhuma conta encontrada")}</option>
                        )}
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5 text-[#0077b5]" />{" "}
                      {t("Idioma do Perfil")}
                    </label>
                    <div className="relative">
                      <select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        className="w-full h-12 px-4 bg-white border border-gray-300 rounded-xl text-sm font-semibold text-gray-900 outline-hidden focus:border-[#0077b5] focus:ring-2 focus:ring-[#0077b5]/10 appearance-none transition-all"
                      >
                        <option value="pt">{t("Português (Brasil)")}</option>
                        <option value="en">English (US/UK)</option>
                        <option value="es">{t("Español")}</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Goal ("Meta") Card */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xs">
                <h2 className="text-lg font-bold text-gray-900 mb-1">
                  {t("Meta")}
                </h2>
                <p className="text-sm text-gray-500 mb-6">
                  {t("Que resultados você gostaria de alcançar com o anúncio?")}
                </p>

                <div className="flex flex-col gap-6">
                  {/* Option 1: Mais impressões */}
                  <label className="flex items-start gap-4 cursor-pointer group">
                    <input
                      type="radio"
                      name="objective"
                      value="impressions"
                      checked={objective === "impressions"}
                      onChange={() => setObjective("impressions")}
                      className="mt-1 w-5 h-5 text-[#0077b5] border-gray-300 focus:ring-[#0077b5] accent-[#0077b5]"
                    />
                    <div className="flex-1 flex justify-between items-start">
                      <div>
                        <span className="block text-sm font-bold text-gray-900">
                          {t("Mais impressões")}
                        </span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          {t("Alcance mais pessoas com sua publicação")}
                        </span>
                      </div>
                      <span className="bg-[#e1f0e4] text-[#136c32] px-2.5 py-1 rounded-sm text-[9px] font-black uppercase tracking-wider">
                        {t("Recomendação")}
                      </span>
                    </div>
                  </label>

                  {/* Option 2: Mais engajamento */}
                  <label className="flex items-start gap-4 cursor-pointer group">
                    <input
                      type="radio"
                      name="objective"
                      value="engagement"
                      checked={objective === "engagement"}
                      onChange={() => setObjective("engagement")}
                      className="mt-1 w-5 h-5 text-[#0077b5] border-gray-300 focus:ring-[#0077b5] accent-[#0077b5]"
                    />
                    <div>
                      <span className="block text-sm font-bold text-gray-900">
                        {t("Mais engajamento")}
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {t(
                          "Aumente o engajamento social e o número de seguidores do perfil",
                        )}
                      </span>
                    </div>
                  </label>

                  {/* Option 3: Mais leads */}
                  <label className="flex items-start gap-4 cursor-pointer group">
                    <input
                      type="radio"
                      name="objective"
                      value="leads"
                      checked={objective === "leads"}
                      onChange={() => setObjective("leads")}
                      className="mt-1 w-5 h-5 text-[#0077b5] border-gray-300 focus:ring-[#0077b5] accent-[#0077b5]"
                    />
                    <div>
                      <span className="block text-sm font-bold text-gray-900">
                        {t("Mais leads")}
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {t(
                          "Use um formulário para coletar dados de contato de potenciais clientes",
                        )}
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Conditional Lead Form Selection */}
              {objective === "leads" && (
                <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xs space-y-3 animate-in slide-in-from-top-2 duration-300">
                  <h3 className="text-xs font-bold text-gray-900 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#0077b5]" />{" "}
                    {t("Formulário de Leads LinkedIn")}
                  </h3>
                  <div className="relative">
                    <select
                      value={selectedLeadFormId}
                      onChange={(e) => setSelectedLeadFormId(e.target.value)}
                      className="w-full h-12 px-4 bg-white border border-gray-300 rounded-xl text-sm font-semibold text-gray-900 outline-hidden focus:border-[#0077b5] appearance-none"
                    >
                      {loadingLeadForms ? (
                        <option>{t("Carregando formulários...")}</option>
                      ) : leadForms.length > 0 ? (
                        leadForms.map((form) => (
                          <option key={form.id} value={form.id}>
                            {form.name}
                          </option>
                        ))
                      ) : (
                        <option>
                          {t("Nenhum formulário ativo encontrado")}
                        </option>
                      )}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                  <p className="text-[10px] text-gray-400 font-medium">
                    {t(
                      "Escolha o formulário que os usuários preencherão no anúncio.",
                    )}
                  </p>
                </div>
              )}

              {/* Público (AI Suggested & Collapsible Targeting) */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xs space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-bold text-gray-900">
                    {t("Público")}
                  </h2>
                  <button
                    onClick={() =>
                      setShowAdvancedTargeting(!showAdvancedTargeting)
                    }
                    className="border border-[#0077b5] text-[#0077b5] hover:bg-[#0077b5]/5 px-4 py-1.5 rounded-full text-xs font-bold transition-all"
                  >
                    {showAdvancedTargeting
                      ? t("Ver público sugerido")
                      : t("Criar/Editar público")}
                  </button>
                </div>
                <p className="text-sm text-gray-500">
                  {t("Quem deve ver seu anúncio?")}
                </p>

                <div className="flex">
                  <span className="flex items-center gap-2 bg-white border border-gray-300 rounded-full px-4 py-1.5 text-xs font-semibold text-gray-700">
                    {t("Público sugerido")}
                    <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                  </span>
                </div>

                {/* AI suggested details box */}
                <div className="bg-[#f0f7ff] border border-[#dce6f1] rounded-xl p-5 flex flex-col gap-4">
                  <div className="flex justify-between items-start gap-4">
                    <p className="text-xs text-gray-700 leading-relaxed font-medium">
                      {t(
                        "Segmentação por IA que se adapta com base no seu conteúdo e objetivo. Inclui mais do que apenas configurações como idioma e localidade.",
                      )}{" "}
                      <span className="text-[#0077b5] font-bold hover:underline cursor-pointer">
                        {t("Saiba mais")}
                      </span>
                    </p>
                    <button
                      onClick={() =>
                        setShowAdvancedTargeting(!showAdvancedTargeting)
                      }
                      className="text-[#0077b5] hover:underline font-bold text-xs whitespace-nowrap"
                    >
                      {t("Editar")}
                    </button>
                  </div>

                  <div className="border-t border-[#dce6f1]/60 pt-3 flex flex-col gap-2">
                    <div className="text-xs text-gray-700 font-medium">
                      <span className="font-bold text-gray-500">
                        {t("Idioma do perfil:")}
                      </span>{" "}
                      {language === "pt"
                        ? t("Português")
                        : language === "en"
                          ? t("Inglês")
                          : t("Espanhol")}
                    </div>
                    <div className="text-xs text-gray-700 font-medium">
                      <span className="font-bold text-gray-500">
                        {t("Localidades:")}
                      </span>{" "}
                      {selectedLocations.map((l) => l.name).join(", ") ||
                        t("Brasil")}
                    </div>
                    {selectedJobTitles.length > 0 && (
                      <div className="text-xs text-gray-700 font-medium">
                        <span className="font-bold text-gray-500">
                          {t("Cargos:")}
                        </span>{" "}
                        {selectedJobTitles.map((jt) => jt.name).join(", ")}
                      </div>
                    )}
                    {selectedIndustries.length > 0 && (
                      <div className="text-xs text-gray-700 font-medium">
                        <span className="font-bold text-gray-500">
                          {t("Setores:")}
                        </span>{" "}
                        {selectedIndustries.map((i) => i.name).join(", ")}
                      </div>
                    )}
                    {selectedSeniorities.length > 0 && (
                      <div className="text-xs text-gray-700 font-medium">
                        <span className="font-bold text-gray-500">
                          {t("Senioridade:")}
                        </span>{" "}
                        {selectedSeniorities.map((s) => s.name).join(", ")}
                      </div>
                    )}
                  </div>
                </div>

                {/* Collapsible Advanced B2B Targeting Inputs */}
                {showAdvancedTargeting && (
                  <div className="pt-4 border-t border-gray-100 space-y-6 animate-in slide-in-from-top-4 duration-300">
                    <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#0077b5]" />{" "}
                      {t("Ajustar Segmentação Avançada B2B")}
                    </h3>

                    <div className="space-y-6 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                      {/* Locations Search */}
                      <TargetingSearchField
                        label={t("Localidade Alvo")}
                        icon={MapPin}
                        query={locationSearch}
                        setQuery={setLocationSearch}
                        results={locationResults}
                        selected={selectedLocations}
                        searching={searchingLocations}
                        placeholder={t("Ex: Brasil, São Paulo...")}
                        onAdd={(item: any) =>
                          addTarget(
                            item,
                            selectedLocations,
                            setSelectedLocations,
                            setLocationSearch,
                            setLocationResults,
                          )
                        }
                        onRemove={(urn: string) =>
                          setSelectedLocations(
                            selectedLocations.filter((i) => i.urn !== urn),
                          )
                        }
                      />

                      {/* Job Titles Search */}
                      <TargetingSearchField
                        label={t("Cargos Profissionais")}
                        icon={Briefcase}
                        query={jobTitleSearch}
                        setQuery={setJobTitleSearch}
                        results={jobTitleResults}
                        selected={selectedJobTitles}
                        searching={searchingJobTitles}
                        placeholder={t("Ex: Diretor, Gestor...")}
                        onAdd={(item: any) =>
                          addTarget(
                            item,
                            selectedJobTitles,
                            setSelectedJobTitles,
                            setJobTitleSearch,
                            setJobTitleResults,
                          )
                        }
                        onRemove={(urn: string) =>
                          setSelectedJobTitles(
                            selectedJobTitles.filter((i) => i.urn !== urn),
                          )
                        }
                      />

                      {/* Industries Search */}
                      <TargetingSearchField
                        label={t("Setores de Atuação")}
                        icon={Building}
                        query={industrySearch}
                        setQuery={setIndustrySearch}
                        results={industryResults}
                        selected={selectedIndustries}
                        searching={searchingIndustries}
                        placeholder={t("Ex: TI, Finanças...")}
                        onAdd={(item: any) =>
                          addTarget(
                            item,
                            selectedIndustries,
                            setSelectedIndustries,
                            setIndustrySearch,
                            setIndustryResults,
                          )
                        }
                        onRemove={(urn: string) =>
                          setSelectedIndustries(
                            selectedIndustries.filter((i) => i.urn !== urn),
                          )
                        }
                      />

                      {/* Seniorities Search */}
                      <TargetingSearchField
                        label={t("Nível de Senioridade")}
                        icon={GraduationCap}
                        query={senioritySearch}
                        setQuery={setSenioritySearch}
                        results={seniorityResults}
                        selected={selectedSeniorities}
                        searching={searchingSeniorities}
                        placeholder={t("Ex: Sênior, VP...")}
                        onAdd={(item: any) =>
                          addTarget(
                            item,
                            selectedSeniorities,
                            setSelectedSeniorities,
                            setSenioritySearch,
                            setSeniorityResults,
                          )
                        }
                        onRemove={(urn: string) =>
                          setSelectedSeniorities(
                            selectedSeniorities.filter((i) => i.urn !== urn),
                          )
                        }
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Investment & Schedule Card */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xs space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-black text-gray-500 uppercase tracking-wider flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-600" />{" "}
                    {t("Investimento & Cronograma")}
                  </h3>
                  <div className="bg-emerald-50 px-4 py-1.5 rounded-full border border-emerald-100">
                    <span className="text-xs font-black text-emerald-600">
                      {t("Total Estimado: R$ {value}", {
                        value: totalBudget.toFixed(2),
                      })}
                    </span>
                  </div>
                </div>

                {/* Dates Selection */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] text-gray-500 uppercase font-black tracking-wider ml-1">
                      {t("Início da Veiculação")}
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-xs font-bold text-gray-900 outline-hidden focus:border-[#0077b5] transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] text-gray-500 uppercase font-black tracking-wider ml-1">
                      {t("Término (Previsão)")}
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-xs font-bold text-gray-900 outline-hidden focus:border-[#0077b5] transition-all"
                    />
                  </div>
                </div>

                {/* Daily Budget Slider */}
                <div className="space-y-4 pt-2">
                  <div className="flex justify-between text-xs font-bold uppercase text-gray-500">
                    <span>{t("Orçamento Diário (R$)")}</span>
                    <div className="flex items-center gap-1.5 text-purple-600">
                      <span className="text-sm font-bold">R$</span>
                      <input
                        type="number"
                        value={dailyBudget}
                        onChange={(e) =>
                          setDailyBudget(parseInt(e.target.value) || 0)
                        }
                        className="w-20 bg-transparent text-right font-black text-base outline-hidden text-[#0077b5] border-b border-gray-200"
                      />
                    </div>
                  </div>
                  <input
                    type="range"
                    min="20"
                    max="1000"
                    step="10"
                    value={dailyBudget}
                    onChange={(e) => setDailyBudget(parseInt(e.target.value))}
                    className="w-full accent-[#0077b5] h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                    <span>{t("Mín R$ 20")}</span>
                    <span>{t("Máx R$ 1000")}</span>
                  </div>
                </div>

                {/* Additional Toggles */}
                <div className="grid grid-cols-2 gap-6 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="space-y-0.5">
                      <h4 className="text-xs font-bold text-gray-900">
                        {t("Expansão de Público")}
                      </h4>
                      <p className="text-[9px] text-gray-400 font-semibold">
                        {t("Alcançar perfis similares")}
                      </p>
                    </div>
                    <button
                      onClick={() => setAudienceExpansion(!audienceExpansion)}
                      className={cn(
                        "w-12 h-6 rounded-full transition-all relative",
                        audienceExpansion ? "bg-[#0077b5]" : "bg-gray-200",
                      )}
                    >
                      <div
                        className={cn(
                          "w-4 h-4 bg-white rounded-full absolute top-1 transition-all shadow-xs",
                          audienceExpansion ? "right-1" : "left-1",
                        )}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="space-y-0.5">
                      <h4 className="text-xs font-bold text-gray-900">
                        {t("Audience Network")}
                      </h4>
                      <p className="text-[9px] text-gray-400 font-semibold">
                        {t("Exibir em apps parceiros")}
                      </p>
                    </div>
                    <button
                      onClick={() => setAudienceNetwork(!audienceNetwork)}
                      className={cn(
                        "w-12 h-6 rounded-full transition-all relative",
                        audienceNetwork ? "bg-[#0077b5]" : "bg-gray-200",
                      )}
                    >
                      <div
                        className={cn(
                          "w-4 h-4 bg-white rounded-full absolute top-1 transition-all shadow-xs",
                          audienceNetwork ? "right-1" : "left-1",
                        )}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column (Estimates & Real-time Live Preview) - 5 cols */}
            <div className="lg:col-span-5 flex flex-col gap-6">
              {/* Predicted Results Card (Replicated exactly from the screenshot) */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xs space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-gray-900 tracking-tight uppercase flex items-center gap-1.5">
                    {t("Resultados previstos")}{" "}
                    <HelpCircle className="w-4 h-4 text-gray-300 cursor-help" />
                  </h3>
                  <div className="p-2 bg-purple-50 rounded-xl text-purple-600">
                    <TrendingUp className="w-4 h-4" />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 border-b border-gray-100 pb-6">
                  <div>
                    <span className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider border-b border-dotted border-gray-300 pb-1 cursor-help w-max">
                      {t("Tamanho do público")}
                    </span>
                    <span className="block text-xl font-extrabold text-gray-900 mt-2">
                      {loadingEstimate ? (
                        <span className="text-xs text-gray-400 animate-pulse">
                          {t("Carregando...")}
                        </span>
                      ) : (
                        getAudienceSize()
                      )}
                    </span>
                  </div>

                  <div>
                    <span className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider pb-1 w-max">
                      {t("Investimento")}
                    </span>
                    <span className="block text-sm font-extrabold text-gray-900 mt-2">
                      R$ {totalBudget.toLocaleString("pt-BR")}
                    </span>
                  </div>

                  <div>
                    <span className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider pb-1 w-max">
                      {t("Impressões")}
                    </span>
                    <span className="block text-xs font-extrabold text-gray-900 mt-2 text-ellipsis overflow-hidden whitespace-nowrap">
                      {loadingEstimate ? (
                        <span className="text-xs text-gray-400 animate-pulse">
                          {t("Carregando...")}
                        </span>
                      ) : (
                        getImpressionsRange()
                      )}
                    </span>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-[10px] text-gray-500 leading-relaxed font-medium">
                    {t(
                      "Os resultados exibidos refletem o investimento e os principais resultados relativos a",
                    )}{" "}
                    <span className="font-bold">
                      {t("{duration} dias", { duration })}
                    </span>
                    .{" "}
                    {t(
                      "Os resultados previstos são uma estimativa de tendência e não garantem o desempenho.",
                    )}{" "}
                    <span className="text-[#0077b5] font-bold cursor-pointer hover:underline">
                      {t("Saiba mais")}
                    </span>
                  </p>
                </div>
              </div>

              {/* Pré-visualizar anúncio Card (Beautiful Live Mockup Card) */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xs flex flex-col gap-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-base font-bold text-gray-900">
                    {t("Pré-visualizar anúncio")}
                  </h2>
                </div>

                <div>
                  <span className="inline-flex items-center gap-2 bg-white border border-gray-300 rounded-full px-4 py-1.5 text-xs font-semibold text-gray-700">
                    {t("Feed em dispositivos móveis")}
                    <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                  </span>
                </div>

                {/* Authentic LinkedIn Live Preview Mockup */}
                <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-xs flex flex-col gap-3">
                  <p className="text-[10px] text-gray-500 leading-normal border-b border-gray-100 pb-2">
                    <span className="font-semibold text-gray-700">
                      André Miranda da Cruz
                    </span>
                    ,{" "}
                    <span className="font-semibold text-gray-700">
                      Allan Alves Antunes
                    </span>{" "}
                    {t("e mais")}{" "}
                    <span className="font-semibold text-gray-700">
                      {t("162 conexões")}
                    </span>{" "}
                    {t("seguem esta Page")}
                  </p>

                  <div className="flex items-start gap-2.5">
                    {/* Brand avatar */}
                    <div className="w-10 h-10 bg-[#0077b5] rounded-lg flex items-center justify-center font-bold text-white text-lg shadow-xs">
                      V
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-bold text-gray-900 truncate">
                        Virtus Automation
                      </h4>
                      <p className="text-[10px] text-gray-500">
                        {t("409 seguidores")}
                      </p>
                      <p className="text-[10px] text-gray-500 flex items-center gap-1">
                        {t("Promovida")} •{" "}
                        <Globe className="w-3 h-3 text-gray-400" />
                      </p>
                    </div>
                  </div>

                  {/* Caption Text - Dynamic */}
                  <p className="text-xs text-gray-800 leading-relaxed font-normal">
                    {post.content ? (
                      <>
                        {post.content.length > 90 ? (
                          <>
                            {post.content.substring(0, 90)}...{" "}
                            <span className="text-gray-500 font-bold hover:underline cursor-pointer">
                              {t("mais")}
                            </span>
                          </>
                        ) : (
                          post.content
                        )}
                      </>
                    ) : (
                      t(
                        "Social Media que responde, engaja e gera leads profissionalmente.",
                      )
                    )}
                  </p>

                  {/* Creative Banner Box */}
                  <div className="w-full rounded-lg overflow-hidden border border-gray-100 aspect-video relative flex items-center justify-center bg-linear-to-br from-[#0b1320] to-[#121c2c]">
                    {getPostImageUrl() ? (
                      <img
                        src={getPostImageUrl()!}
                        alt={t("Pré-visualização do criativo do anúncio")}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-center p-6 text-white w-full h-full">
                        <span className="text-[10px] font-bold tracking-widest text-[#0077b5]/80 uppercase">
                          Virtus Automation
                        </span>
                        <h3 className="text-base font-black uppercase text-center mt-2 tracking-wide text-white leading-tight max-w-[200px]">
                          {post.content?.split("\n")[0]?.substring(0, 40) ||
                            t("Filtre Interessados Reais")}
                        </h3>
                      </div>
                    )}
                  </div>

                  {/* LinkedIn Bottom Promoted CTA Bar */}
                  <div className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-4 py-2.5">
                    <div className="flex-1 min-w-0">
                      <span className="block text-[10px] text-gray-400 uppercase tracking-widest truncate">
                        virtusautomation.com
                      </span>
                      <span className="block text-xs font-bold text-gray-700 truncate">
                        {objective === "leads"
                          ? t("Formulário de Contato Inteligente")
                          : t("Soluções de Automação para Negócios")}
                      </span>
                    </div>
                    <button className="shrink-0 bg-white border border-[#0077b5] text-[#0077b5] hover:bg-[#0077b5]/5 px-4 py-1.5 rounded-full text-xs font-bold transition-all shadow-xs">
                      {objective === "leads"
                        ? t("Cadastrar-se")
                        : t("Saiba mais")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-10 border-t border-gray-200 bg-white flex items-center justify-between shadow-inner">
          <div className="flex flex-col">
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              {t("Investimento Total Estimado")}
            </span>
            <span className="text-3xl font-black text-gray-900 tracking-tighter">
              R$ {totalBudget.toFixed(2)}
            </span>
          </div>
          <div className="flex items-center gap-8">
            <button
              onClick={onClose}
              className="text-xs font-black text-gray-400 hover:text-gray-950 transition-colors uppercase tracking-widest"
            >
              {t("Cancelar")}
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !selectedAccountId}
              className="px-16 py-5 rounded-2xl bg-[#0077b5] text-white font-bold text-xs uppercase tracking-widest shadow-lg shadow-[#0077b5]/20 hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:hover:scale-100 flex items-center gap-3"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t("Ativando...")}
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  {t("Ativar Campanha")}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Component for Search Fields
const TargetingSearchField = ({
  label,
  icon: Icon,
  query,
  setQuery,
  results,
  selected,
  searching,
  onAdd,
  onRemove,
  placeholder,
}: any) => (
  <div className="space-y-2 relative">
    <label className="text-[10px] text-gray-500 uppercase font-black tracking-widest ml-1 flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 text-[#0077b5]" /> {label}
    </label>
    <div className="relative group">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
        <Search className="w-4 h-4" />
      </div>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        className="w-full h-11 pl-11 pr-10 bg-white border border-gray-300 rounded-xl text-xs font-medium text-gray-900 outline-hidden focus:border-[#0077b5] transition-all shadow-xs"
      />
      {searching && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2">
          <div className="w-3.5 h-3.5 border-2 border-[#0077b5] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {results.length > 0 && (
        <div className="absolute z-50 left-0 right-0 top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto custom-scrollbar p-2 animate-in slide-in-from-top-2">
          {results.map((res: any) => (
            <button
              key={res.urn}
              onClick={() => onAdd(res)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg text-xs font-semibold text-gray-900 transition-colors flex items-center justify-between"
            >
              <span>{res.name}</span>
              <Plus className="w-4 h-4 text-gray-300 hover:text-[#0077b5]" />
            </button>
          ))}
        </div>
      )}
    </div>

    <div className="flex flex-wrap gap-2 pt-1 min-h-[30px]">
      {selected.map((item: any) => (
        <div
          key={item.urn}
          className="flex items-center gap-2 bg-[#f0f7ff] border border-[#dce6f1] text-[#0077b5] px-3.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-tight shadow-xs hover:shadow-md transition-all"
        >
          {item.name}
          <button
            onClick={() => onRemove(item.urn)}
            className="text-blue-300 hover:text-red-500 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  </div>
);

export default BoostLinkedInModal;
